import React, { useEffect, useState } from 'react'
import { Check, X, Settings, Loader2, Shield, Zap, Clock } from 'lucide-react'
import { useModules } from '../../../../hooks/features/modules/useModules'
import PageListLayout from '../../../../layouts/PageListLayout'
import { alertSuccess, alertError } from '../../../../helpers/alertError'

export default function EntrepriseModulesPage() {
    const {
        modules,
        entrepriseModules,
        getEntrepriseModules,
        activateModule,
        deactivateModule,
        loading
    } = useModules()

    const [isToggling, setIsToggling] = useState<string | null>(null)

  

    const handleToggle = async (moduleId: string, isEnabled: boolean) => {
        setIsToggling(moduleId)
        try {
            if (isEnabled) {
                await deactivateModule(moduleId)
                alertSuccess('Module désactivé avec succès')
            } else {
                await activateModule(moduleId)
                alertSuccess('Module activé avec succès')
            }
            await getEntrepriseModules()
        } catch (error) {
            alertError('Erreur lors du changement de statut')
        } finally {
            setIsToggling(null)
        }
    }

    // Fusionner les modules disponibles avec leur statut pour l'entreprise
    const modulesWithStatus = modules.map(module => {
        const entrepriseModule = entrepriseModules.find(em => em.id === module.id)
        return {
            ...module,
            is_enabled: entrepriseModule?.is_enabled || false,
            activated_at: entrepriseModule?.activated_at || null,
        }
    })

    const activeModules = modulesWithStatus.filter(m => m.is_enabled)
    const inactiveModules = modulesWithStatus.filter(m => !m.is_enabled)

    return (
        <PageListLayout title="">
            <div className="px-6 py-5 bg-white border-b border-slate-100">
                <div className="max-w-7xl mx-auto">
                    <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">
                        Gestion des modules
                    </h1>
                    <p className="text-sm text-slate-400 font-light mt-0.5">
                        Activez ou désactivez les modules disponibles pour votre entreprise
                    </p>
                    <div className="flex gap-4 mt-2">
                        <span className="text-xs text-green-600">
                            Actifs: {activeModules.length}
                        </span>
                        <span className="text-xs text-gray-500">
                            Disponibles: {modules.length}
                        </span>
                    </div>
                </div>
            </div>

            <div className="px-6 py-8 max-w-4xl mx-auto">
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <Loader2 className="w-7 h-7 animate-spin text-primary" />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Modules actifs */}
                        {activeModules.length > 0 && (
                            <div>
                                <h2 className="text-sm font-semibold text-green-600 mb-3 flex items-center gap-2">
                                    <Check size={16} />
                                    Modules actifs
                                </h2>
                                <div className="space-y-2">
                                    {activeModules.map((module) => (
                                        <div
                                            key={module.id}
                                            className="bg-white border border-green-100 rounded-xl p-4 flex items-center justify-between hover:border-green-200 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                                                    <Shield size={18} />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-800">{module.name}</p>
                                                    <p className="text-xs text-slate-500">{module.description}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                    Actif
                                                </span>
                                                <button
                                                    onClick={() => handleToggle(module.id, true)}
                                                    disabled={isToggling === module.id}
                                                    className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                >
                                                    {isToggling === module.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        'Désactiver'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Modules inactifs */}
                        {inactiveModules.length > 0 && (
                            <div>
                                <h2 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                                    <X size={16} />
                                    Modules inactifs
                                </h2>
                                <div className="space-y-2">
                                    {inactiveModules.map((module) => (
                                        <div
                                            key={module.id}
                                            className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between hover:border-gray-300 transition-colors opacity-75 hover:opacity-100"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center">
                                                    <Zap size={18} />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-800">{module.name}</p>
                                                    <p className="text-xs text-slate-500">{module.description}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                    Inactif
                                                </span>
                                                <button
                                                    onClick={() => handleToggle(module.id, false)}
                                                    disabled={isToggling === module.id}
                                                    className="px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                                >
                                                    {isToggling === module.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        'Activer'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Aucun module disponible */}
                        {modules.length === 0 && !loading && (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Settings size={24} className="text-gray-400" />
                                </div>
                                <p className="text-gray-500">Aucun module disponible</p>
                                <p className="text-sm text-gray-400 mt-1">Contactez votre administrateur</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </PageListLayout>
    )
}